import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useHelpCategories, useHelpArticles, HelpArticle, HelpCategory } from "@/hooks/useHelpCenter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Rocket,
  User,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Shield,
  HelpCircle,
  ChevronRight,
  ArrowLeft,
  Clock,
  FileText,
} from "lucide-react";

const iconMap: Record<string, React.ElementType> = {
  Rocket,
  User,
  LayoutDashboard,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  MessageSquare,
  Shield,
  HelpCircle,
};

type SortOption = "newest" | "oldest" | "title-asc";

// Calculate read time based on content length (roughly 200 words per minute)
function calculateReadTime(article: HelpArticle): number {
  let wordCount = 0;
  
  // Count words in title and summary
  if (article.title) wordCount += article.title.split(/\s+/).length;
  if (article.summary) wordCount += article.summary.split(/\s+/).length;
  
  // Parse body content
  if (article.body) {
    try {
      const content = JSON.parse(article.body);
      if (content?.blocks && Array.isArray(content.blocks)) {
        content.blocks.forEach((block: { type: string; content?: string; title?: string }) => {
          if (block.content) wordCount += block.content.split(/\s+/).length;
          if (block.title) wordCount += block.title.split(/\s+/).length;
        });
      }
    } catch {
      // Legacy HTML content - strip tags and count words
      const textContent = article.body.replace(/<[^>]*>/g, ' ');
      wordCount += textContent.split(/\s+/).filter(Boolean).length;
    }
  }
  
  // Calculate minutes (minimum 1 minute)
  return Math.max(1, Math.ceil(wordCount / 200));
}

interface CategoryArticlesProps {
  categoryId: string;
}

export function CategoryArticles({ categoryId }: CategoryArticlesProps) {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  
  const { data: categories, isLoading: categoriesLoading } = useHelpCategories();
  const { data: articles, isLoading: articlesLoading } = useHelpArticles(categoryId);

  const category = categories?.find(c => c.id === categoryId);
  const publishedArticles = articles?.filter(a => a.is_published && !a.is_archived);
  const Icon = iconMap[category?.icon || 'HelpCircle'] || HelpCircle;
  
  // Get other active categories for "Related Categories" section
  const otherCategories = categories?.filter(c => c.is_active && c.id !== categoryId);

  // Sort articles based on selected option
  const sortedArticles = useMemo(() => {
    if (!publishedArticles) return [];
    
    return [...publishedArticles].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "oldest":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "title-asc":
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });
  }, [publishedArticles, sortBy]);

  if (categoriesLoading || articlesLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-6 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Category Not Found</h2>
        <p className="text-muted-foreground mb-4">This category may have been removed or is not available.</p>
        <Button onClick={() => navigate("/help")}>Back to Help Center</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="space-y-3 sm:space-y-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate("/help")}
          className="gap-2 -ml-2 h-10 px-3"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back to Help Center</span>
          <span className="sm:hidden">Back</span>
        </Button>
        
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="p-2.5 sm:p-3 rounded-xl bg-primary/10 shrink-0">
            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold mb-1 leading-tight">{category.name}</h1>
            {category.description && (
              <p className="text-sm sm:text-base text-muted-foreground line-clamp-2">{category.description}</p>
            )}
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4 shrink-0" />
              <span>{sortedArticles.length} article{sortedArticles.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - stack sidebar on mobile */}
      <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1fr_280px]">
        {/* Articles List */}
        <div className="space-y-3 sm:space-y-4">
          {/* Sorting Controls */}
          {sortedArticles.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Showing {sortedArticles.length} article{sortedArticles.length !== 1 ? 's' : ''}
              </p>
              <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="title-asc">Title A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Empty State */}
          {sortedArticles.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 sm:py-12 text-center px-4">
                <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-semibold mb-2">No Articles Yet</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
                  There are no published articles in this category yet. Check back later or explore other categories.
                </p>
                <Button variant="outline" onClick={() => navigate("/help")} className="h-11 sm:h-10">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Help Center
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {sortedArticles.map(article => {
                const readTime = calculateReadTime(article);
                
                return (
                  <Card
                    key={article.id}
                    className="cursor-pointer transition-all duration-200 hover:bg-muted/50 hover:border-primary/30 hover:shadow-md active:scale-[0.99]"
                    onClick={() => navigate(`/help/article/${article.id}`)}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground mb-1 text-sm sm:text-base leading-tight">{article.title}</h4>
                          {article.summary && (
                            <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-2">
                              {article.summary}
                            </p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {readTime} min read
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Related Categories Sidebar - collapsible on mobile */}
        {otherCategories && otherCategories.length > 0 && (
          <div className="space-y-4 lg:sticky lg:top-4">
            <Card>
              <CardHeader className="pb-2 sm:pb-3 p-4 sm:p-6">
                <CardTitle className="text-base">Other Categories</CardTitle>
                <CardDescription className="text-sm">Explore more help topics</CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-1.5">
                {otherCategories.slice(0, 5).map(cat => {
                  const CatIcon = iconMap[cat.icon || 'HelpCircle'] || HelpCircle;
                  
                  return (
                    <Button
                      key={cat.id}
                      variant="ghost"
                      className="w-full justify-start gap-2 h-10 sm:h-auto sm:py-2 px-3"
                      onClick={() => navigate(`/help/category/${cat.id}`)}
                    >
                      <CatIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="truncate text-sm">{cat.name}</span>
                    </Button>
                  );
                })}
                
                {otherCategories.length > 5 && (
                  <Button
                    variant="link"
                    className="w-full text-sm h-10"
                    onClick={() => navigate("/help")}
                  >
                    View all categories
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoryArticles;
